/**
 * Centro de notificaciones backend-driven.
 *
 * Fuente de verdad: la tabla `notifications` en la BD.
 * Este contexto SOLO cachea en memoria lo que el backend devuelve.
 * NO usa localStorage. NO depende del navegador.
 *
 * API pública (mismo shape que antes para no romper consumidores):
 *   - items           últimos items cargados (paginados)
 *   - unreadCount     cuántas no leídas (para el badge)
 *   - pendingToasts   cuántas notifs todavía no mostraron toast
 *   - loading, error
 *   - refresh()       recargar la lista
 *   - addNotification()   (compat: en realidad llama a refresh() porque
 *                          el backend ya generó la notif)
 *   - markAsRead(id), markAsUnread(id), toggleRead(id)
 *   - markAllAsRead()
 *   - marcarToastMostrado(id)
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react"

const NotificationContext = createContext(null)

// Limpia claves viejas que quedaron del sistema anterior (localStorage)
function limpiarStorageViejo() {
  try {
    localStorage.removeItem("idaas_notifications")
    localStorage.removeItem("idaas_notif_sent_ids")
  } catch (_) { /* silencioso */ }
}

function authHeaders() {
  const token = localStorage.getItem("token") || ""
  if (!token) return {}
  return { Authorization: "Bearer " + token }
}

/**
 * Maneja respuestas 401/422 del backend.
 *  401 -> token ausente
 *  422 -> token expirado o invalido (comportamiento de Flask-JWT-Extended)
 * En ambos casos: limpia el token y redirige a /login una sola vez.
 */
let _redirigiendo = false
function manejarSesionExpirada(status) {
  if (status !== 401 && status !== 422) return false
  if (_redirigiendo) return true
  _redirigiendo = true
  try { localStorage.removeItem("token") } catch (_) { /* noop */ }
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.href = "/login"
  }
  return true
}


export function NotificationProvider({ children }) {
  const [items, setItems] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [pendingToasts, setPendingToasts] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const intervalRef = useRef(null)

  // Limpiar claves del sistema anterior una sola vez
  useEffect(() => { limpiarStorageViejo() }, [])

  // Carga la lista (30 items)
  const refresh = useCallback(async () => {
    // Sin token -> no hay sesion -> no disparamos fetch (evita 422 en /login)
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null
    if (!token) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const res = await fetch("/api/notifications?perPage=30", {
        headers: authHeaders(),
      })
      if (manejarSesionExpirada(res.status)) {
        setLoading(false)
        return
      }
      if (!res.ok) throw new Error("HTTP " + res.status)
      const data = await res.json()
      setItems(data.items || [])
      setUnreadCount(data.unreadCount || 0)
    } catch (e) {
      setError(e.message || "Error cargando notificaciones")
    } finally {
      setLoading(false)
    }
  }, [])

  // Polling liviano al count (badge + toasts pendientes)
  const refreshCount = useCallback(async () => {
    // Sin token -> no hay sesion -> no disparamos fetch
    const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null
    if (!token) return
    try {
      const res = await fetch("/api/notifications/count", {
        headers: authHeaders(),
      })
      if (manejarSesionExpirada(res.status)) return
      if (!res.ok) return
      const data = await res.json()
      setUnreadCount(data.unread || 0)
      setPendingToasts(data.pendingToasts || 0)
    } catch (_) { /* silencioso */ }
  }, [])

  // Al montar: primer fetch completo + count
  useEffect(() => {
    refresh()
    refreshCount()
  }, [refresh, refreshCount])

  // Polling cada 20s al count (no a la lista completa, más liviano)
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return
      refreshCount()
    }, 120000) // F9.101a: 120s (antes 20s) para evitar rate limit

    function onVisibility() {
      if (document.visibilityState === "visible") refreshCount()
    }
    document.addEventListener("visibilitychange", onVisibility)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [refreshCount])

  // ─── Acciones ───

  const markAsRead = useCallback(async (id) => {
    // Optimistic UI
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, leida: true } : n))
    setUnreadCount((c) => Math.max(0, c - 1))
    try {
      await fetch(`/api/notifications/${id}/read`, {
        method: "POST", headers: authHeaders(),
      })
    } catch (_) { /* silencioso */ }
  }, [])

  const markAsUnread = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => n.id === id ? { ...n, leida: false } : n))
    setUnreadCount((c) => c + 1)
    try {
      await fetch(`/api/notifications/${id}/unread`, {
        method: "POST", headers: authHeaders(),
      })
    } catch (_) { /* silencioso */ }
  }, [])

  const toggleRead = useCallback((id) => {
    const item = items.find((n) => n.id === id)
    if (!item) return
    if (item.leida) return markAsUnread(id)
    return markAsRead(id)
  }, [items, markAsRead, markAsUnread])

  const markAllAsRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, leida: true })))
    setUnreadCount(0)
    try {
      await fetch("/api/notifications/mark-all-read", {
        method: "POST", headers: authHeaders(),
      })
    } catch (_) { /* silencioso */ }
  }, [])

  const marcarToastMostrado = useCallback(async (id) => {
    setPendingToasts((c) => Math.max(0, c - 1))
    try {
      await fetch(`/api/notifications/${id}/toast-shown`, {
        method: "POST", headers: authHeaders(),
      })
    } catch (_) { /* silencioso */ }
  }, [])

  // Compat: el consumidor puede llamar addNotification() pero el backend ya
  // creó la notif. Simplemente refrescamos.
  const addNotification = useCallback(() => {
    refreshCount()
    refresh()
  }, [refresh, refreshCount])

  const value = useMemo(() => ({
    items,
    unreadCount,
    pendingToasts,
    loading,
    error,
    refresh,
    refreshCount,
    addNotification,
    markAsRead,
    markAsUnread,
    toggleRead,
    markAllAsRead,
    marcarToastMostrado,
  }), [
    items, unreadCount, pendingToasts, loading, error,
    refresh, refreshCount, addNotification,
    markAsRead, markAsUnread, toggleRead, markAllAsRead, marcarToastMostrado,
  ])

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const ctx = useContext(NotificationContext)
  if (!ctx) {
    // Fallback silencioso
    return {
      items: [], unreadCount: 0, pendingToasts: 0, loading: false, error: null,
      refresh: () => {}, refreshCount: () => {}, addNotification: () => {},
      markAsRead: () => {}, markAsUnread: () => {}, toggleRead: () => {},
      markAllAsRead: () => {}, marcarToastMostrado: () => {},
    }
  }
  return ctx
}
